import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  addStaffingRow, applyStaffingToBudget, deleteStaffingRow, getProject,
  getProjectStaffing, getTeamMembers, setStaffingWeeks, shiftProjectSchedule,
  updateProject,
} from '../../api/client'
import { reordered } from '../capacity/sheet'
import BackfillModal from './BackfillModal'
import ColumnContextMenu from './ColumnContextMenu'
import { dateLabel, isoOf, mondayOf, monthLabel, monthYearLabel, num, weekLabel } from './format'
import { HoursCell } from './HoursCell'
import ProjectMonthlyChart from './ProjectMonthlyChart'
import { CELL_W, META_W, NAME_W, ROLE_W } from './layout'
import ReplaceModal from './ReplaceModal'
import RowContextMenu from './RowContextMenu'
import StaffingImportModal from './StaffingImportModal'
import { TotalsRow } from './TotalsRow'
import { useRowReorder } from './useRowReorder'
import { useStaffingViewport } from './useStaffingViewport'
import { isoWeekNumber } from './weeks'

export default function StaffingTab({ projectId, canEdit }) {
  const { t } = useTranslation(['engineering', 'common', 'clevel'])
  const [data, setData] = useState(null)
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showActual, setShowActual] = useState(true)
  const [adding, setAdding] = useState('')
  const [replacing, setReplacing] = useState(null)   // the row being handed over
  const [importing, setImporting] = useState(false)
  const [backfilling, setBackfilling] = useState(false)
  const [fullscreen, setFullscreen] = useState(false)
  const [extendWeeks, setExtendWeeks] = useState(8)
  const [extending, setExtending] = useState(false)
  const [shifting, setShifting] = useState(false)
  const [notice, setNotice] = useState('')
  const [menu, setMenu] = useState(null)   // { x, y, row, mode: 'root' | 'pick' | 'fill' }
  const [colMenu, setColMenu] = useState(null)   // { x, y, week, isFirst, isLast }

  useEffect(() => {
    if (!menu && !colMenu && !fullscreen) return undefined
    const close = (e) => {
      if (e.key !== 'Escape') return
      if (menu || colMenu) { setMenu(null); setColMenu(null) }
      else setFullscreen(false)
    }
    document.addEventListener('keydown', close)
    return () => document.removeEventListener('keydown', close)
  }, [menu, colMenu, fullscreen])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [plan, team] = await Promise.all([
        getProjectStaffing(projectId), getTeamMembers({ plannable: true }),
      ])
      setData(plan)
      setMembers(team)
    } catch (err) {
      setError(err.response?.data?.detail || t('staffing.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [projectId, t])

  useEffect(() => { load() }, [load])

  const guard = async (fn) => {
    try { setData(await fn()) }
    catch (err) { setError(err.response?.data?.detail || t('staffing.loadFailed')) }
  }

  const {
    jumpMonth, viewStart, setViewStart, viewWindow, setViewWindow, weekRefs,
    windowActive, weeks, months, visibleMondays, visibleMonthKeys,
    isTrueFirstWeek, isTrueLastWeek, goWindow, shiftWindow, handleJumpTo,
  } = useStaffingViewport(data)

  const { dragging, setDragging, persistOrder, handleDrop } = useRowReorder({
    projectId, data, setData, guard,
  })

  const handleAdd = async (memberId) => {
    if (!memberId) return
    setAdding('')
    // Starts empty rather than pre-filled at the person's full week: a blank
    // row is easy to fill in, but a row already sitting at 40 h everywhere
    // silently overstates the plan until someone notices and trims it.
    await guard(() => addStaffingRow(projectId, { team_member_id: Number(memberId), fill: false }))
  }

  const handleRemove = async (row) => {
    if (!confirm(t('staffing.removeConfirm', { name: row.name }))) return
    await deleteStaffingRow(projectId, row.id)
    load()
  }

  const handleClear = async (row) => {
    if (!confirm(t('staffing.clearRowConfirm', { name: row.name }))) return
    await guard(() => setStaffingWeeks(projectId, row.id, { hours_per_week: null }))
  }

  const handleFill = async (row, hours) => {
    setMenu(null)
    const n = Number(hours)
    if (!Number.isFinite(n) || n < 0) return
    // Every week in the window, each still capped at what that week can hold
    // -- the same rule a single cell obeys, just applied to the whole row.
    await guard(() => setStaffingWeeks(projectId, row.id, { hours_per_week: n }))
  }

  // From the right-click menu: create the row (it lands at the end, same as
  // the picker at the top), then walk it back to sit right after the row
  // that was clicked -- the position the person actually asked for.
  const handleInsertAfter = async (rows, afterRow, memberId) => {
    setMenu(null)
    let created
    try {
      created = await addStaffingRow(projectId, { team_member_id: Number(memberId), fill: false })
    } catch (err) {
      setError(err.response?.data?.detail || t('staffing.loadFailed'))
      return
    }
    const ids = created.rows.map(r => r.id)
    const newId = ids.find(id => !rows.some(r => r.id === id))
    const afterIdx = afterRow ? ids.indexOf(afterRow.id) : -1
    const nextId = newId != null && afterIdx !== -1 ? ids[afterIdx + 1] : undefined
    if (newId == null || nextId == null || nextId === newId) {
      setData(created)   // already at the end, or nothing to reposition
      return
    }
    await persistOrder(created, reordered(ids, newId, nextId))
  }

  // gantt_calendar.axis_range pads the AXIS'S end by one extra week past
  // whatever end_date says (never the start -- see PAD_WEEKS there), so the
  // value written to end_date always has to sit one week short of the last
  // week we actually want on screen. start_date has no such padding.
  const endDateFor = (desiredLastMonday) => {
    const d = new Date(`${desiredLastMonday}T12:00:00`)
    d.setDate(d.getDate() - 7)
    return isoOf(d)
  }

  // The staffing window is the Gantt's own day axis (see project_staffing's
  // module docstring) -- there is no separate "staffing window" to grow or
  // shrink, so every one of these just nudges the project's start/end date
  // and lets the axis follow, the same way adding or moving a task already
  // does. `field`/`iso` decide the direction; the caller decides how far.
  const nudgeProject = async (field, iso, { onlyIf } = {}) => {
    setExtending(true)
    try {
      const project = await getProject(projectId)
      const current = project[field]
      if (!onlyIf || onlyIf(current, iso)) {
        await updateProject(projectId, { [field]: iso })
      }
      const fresh = await getProjectStaffing(projectId)
      setData(fresh)
      return fresh
    } catch (err) {
      setError(err.response?.data?.detail || t('staffing.loadFailed'))
      return null
    } finally {
      setExtending(false)
    }
  }

  const handleExtend = async () => {
    const count = Math.floor(Number(extendWeeks))
    if (!Number.isFinite(count) || count <= 0) return
    const currentWeeks = data?.weeks || []
    const base = currentWeeks.length
      ? new Date(`${currentWeeks[currentWeeks.length - 1].monday}T12:00:00`)
      : new Date()
    base.setDate(base.getDate() + count * 7)   // the desired new LAST week
    // The window can be truncated to a rolling span (see
    // gantt_calendar.axis_range), so what is on screen can lag a farther-out
    // end date that is already stored -- never move it backwards by nudging
    // it to a date that only looks later from here.
    const iso = endDateFor(isoOf(base))
    await nudgeProject('end_date', iso, { onlyIf: (cur, v) => !cur || cur < v })
  }

  // The undo for "added a lot of columns by mistake": drop the last N weeks
  // in one go instead of clicking the single-column × that many times.
  const handleShrink = async () => {
    const requested = Math.floor(Number(extendWeeks))
    if (!Number.isFinite(requested) || requested <= 0) return
    const list = data?.weeks || []
    const count = Math.min(requested, list.length - 1)   // always leave at least one week
    if (count <= 0) return
    const doomed = list.slice(list.length - count)
    const hours = Math.round(doomed.reduce((s, w) => s + (w.hours || 0), 0) * 100) / 100
    if (hours > 0 &&
        !confirm(t('staffing.shrinkWeeksConfirm', { count, hours }))) {
      return
    }
    const newLast = list[list.length - count - 1]
    const fresh = await nudgeProject('end_date', endDateFor(newLast.monday))
    // The axis is the Gantt's own day range (project dates, task dates, and
    // today, whichever is latest) -- moving the project's end date only
    // narrows the window if nothing else is still holding it open. A task or
    // milestone ending on or after the week we tried to drop wins regardless,
    // so the shrink can silently fail to remove anything at all.
    if (fresh && fresh.weeks[fresh.weeks.length - 1]?.monday !== newLast.monday) {
      setError(t('staffing.shrinkWeeksBlocked'))
    }
  }

  // One column at a time, right from the table: the pivot is always whatever
  // is currently the first or last week on screen, so clicking twice adds or
  // removes two weeks in a row without the two calls racing each other --
  // `nudgeProject` reloads before the next click can read a stale pivot.
  const addWeekAt = (edge) => {
    const list = data?.weeks || []
    const pivot = list.length
      ? new Date(`${(edge === 'end' ? list[list.length - 1] : list[0]).monday}T12:00:00`)
      : new Date()
    pivot.setDate(pivot.getDate() + (edge === 'end' ? 7 : -7))   // desired new edge week
    const desired = isoOf(pivot)
    const field = edge === 'end' ? 'end_date' : 'start_date'
    const iso = edge === 'end' ? endDateFor(desired) : desired
    const onlyIf = edge === 'end'
      ? (cur, v) => !cur || cur < v
      : (cur, v) => !cur || cur > v
    nudgeProject(field, iso, { onlyIf })
  }

  const removeWeekAt = async (edge) => {
    const list = data?.weeks || []
    if (list.length < 2) return   // never collapse the window to nothing
    const target = edge === 'end' ? list[list.length - 1] : list[0]
    if ((target.hours || target.headcount) &&
        !confirm(t('staffing.removeWeekConfirm', { week: weekLabel(target.monday) }))) {
      return
    }
    const neighbour = edge === 'end' ? list[list.length - 2] : list[1]   // desired new edge week
    const field = edge === 'end' ? 'end_date' : 'start_date'
    const iso = edge === 'end' ? endDateFor(neighbour.monday) : neighbour.monday
    const fresh = await nudgeProject(field, iso)
    // Same reason handleShrink can fail silently: a Gantt task or milestone
    // sitting right on that edge week keeps the axis open regardless of what
    // the project's own start/end date says.
    const stillThere = fresh && (edge === 'end'
      ? fresh.weeks[fresh.weeks.length - 1]?.monday === target.monday
      : fresh.weeks[0]?.monday === target.monday)
    if (stillThere) {
      setError(t('staffing.removeWeekBlocked'))
    }
  }

  // Moves the whole project -- every Gantt task and milestone, the project's
  // own dates, and every planned staffing week -- by one week, together. The
  // Gantt tab isn't visible from here, so there's nothing else on screen to
  // refresh; it fetches its own fresh dates next time it's opened.
  const handleShiftSchedule = async (weeks) => {
    const direction = weeks < 0 ? t('staffing.shiftScheduleBack') : t('staffing.shiftScheduleForward')
    if (!confirm(t('staffing.shiftScheduleConfirm', { direction }))) {
      return
    }
    setShifting(true)
    setError('')
    setNotice('')
    try {
      const result = await shiftProjectSchedule(projectId, weeks)
      setNotice(t('staffing.shiftScheduleDone', {
        tasks: result.tasks_moved, milestones: result.milestones_moved,
      }))
      const fresh = await getProjectStaffing(projectId)
      setData(fresh)
    } catch (err) {
      setError(err.response?.data?.detail || t('staffing.shiftScheduleFailed'))
    } finally {
      setShifting(false)
    }
  }

  if (loading && !data) {
    return <div className="bg-white rounded-xl border border-gray-200 py-14 text-center text-sm text-gray-400">…</div>
  }
  if (error && !data) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 py-10 text-center space-y-3">
        <p className="text-sm text-red-600">{error}</p>
        <button onClick={load} className="text-sm text-gray-600 underline">{t('staffing.retry')}</button>
      </div>
    )
  }

  const { rows } = data
  const onProject = new Set(rows.map(r => r.team_member_id))
  const available = members.filter(m => !onProject.has(m.id))
  const budgetMatches = data.hours_budget != null
    && Math.abs(data.hours_budget - data.total_hours) < 0.01
  const rowH = showActual ? 34 : 26

  const stickyHead = 'sticky top-0 z-20 bg-white border-b border-gray-200'
  const fixed = (left, width, extra = '') => ({
    className: `sticky z-10 bg-inherit border-r border-gray-100 ${extra}`,
    style: { left, width, minWidth: width, maxWidth: width },
  })

  return (
    <div className={fullscreen
      ? 'fixed inset-0 z-40 bg-gray-50 overflow-auto p-4 space-y-4'
      : 'space-y-4'}>
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{t('staffing.title')}</h2>
          <p className="text-xs text-gray-500 mt-0.5">{t('staffing.subtitle')}</p>
          <p className="text-xs text-gray-400 mt-1">
            {t('staffing.projectDates', {
              start: data.start_date ? dateLabel(data.start_date) : t('staffing.noDate'),
              end: data.end_date ? dateLabel(data.end_date) : t('staffing.noDate'),
            })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input type="checkbox" checked={showActual}
                   onChange={e => setShowActual(e.target.checked)} />
            {t('staffing.showActual')}
          </label>
          <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden text-xs">
            <input type="date" value={viewStart}
                   onChange={e => setViewStart(e.target.value ? mondayOf(e.target.value) : '')}
                   title={t('staffing.viewStartHint')}
                   className="px-2 py-1.5 border-r border-gray-200" />
            {[4, 8, 15, 52].map(size => (
              <button key={size} onClick={() => goWindow(size)}
                      title={t('staffing.viewWindowHint', { count: size })}
                      className={`px-2 py-1.5 ${viewWindow === size
                        ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
                {size}
              </button>
            ))}
            <button onClick={() => setViewWindow(0)}
                    title={t('staffing.viewAllHint')}
                    className={`px-2 py-1.5 border-l border-gray-200 ${!windowActive
                      ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {t('staffing.viewAll')}
            </button>
            {windowActive && (
              <>
                <button onClick={() => shiftWindow(-1)}
                        className="px-2 py-1.5 border-l border-gray-200 hover:bg-gray-50">‹</button>
                <button onClick={() => shiftWindow(1)}
                        className="px-2 py-1.5 border-l border-gray-200 hover:bg-gray-50">›</button>
              </>
            )}
          </div>
          {data.months.length > 0 && (
            <select
              value={jumpMonth}
              onChange={e => handleJumpTo(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
              title={t('staffing.jumpToMonthHint')}
            >
              <option value="">{t('staffing.jumpToMonth')}</option>
              {data.months.map(m => (
                <option key={`${m.year}-${m.month}`} value={`${m.year}-${m.month}`}>
                  {monthYearLabel(m.year, m.month)}
                </option>
              ))}
            </select>
          )}
          {canEdit && (
            <div className="flex items-center gap-1">
              <input type="number" min="1" max="104" value={extendWeeks}
                     onChange={e => setExtendWeeks(e.target.value)}
                     title={t('staffing.weeksCountHint')}
                     className="w-14 border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-right" />
              <button onClick={handleExtend} disabled={extending}
                      title={t('staffing.extendWeeksHint')}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
                {t('staffing.extendWeeksButton')}
              </button>
              <button onClick={handleShrink} disabled={extending}
                      title={t('staffing.shrinkWeeksHint')}
                      className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-red-50 hover:text-red-700 disabled:opacity-50">
                {t('staffing.shrinkWeeksButton')}
              </button>
            </div>
          )}
          {canEdit && (
            <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden text-xs"
                 title={t('staffing.shiftScheduleHint')}>
              <button onClick={() => handleShiftSchedule(-1)} disabled={shifting}
                      className="px-2.5 py-1.5 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                ‹ {t('staffing.shiftScheduleBack')}
              </button>
              <button onClick={() => handleShiftSchedule(1)} disabled={shifting}
                      className="px-2.5 py-1.5 border-l border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                {t('staffing.shiftScheduleForward')} ›
              </button>
            </div>
          )}
          {canEdit && available.length > 0 && (
            <select
              value={adding}
              onChange={e => handleAdd(e.target.value)}
              className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
            >
              <option value="">{t('staffing.selectPerson')}</option>
              {available.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => setImporting(true)}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50"
            title={t('staffing.import.hint')}
          >
            {t('staffing.import.button')}
          </button>
          {canEdit && (
            <button
              onClick={() => setBackfilling(true)}
              className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50"
              title={t('staffing.backfill.hint')}
            >
              {t('staffing.backfill.button')}
            </button>
          )}
          <button onClick={() => setFullscreen(v => !v)}
                  className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">
            {fullscreen ? `⤡ ${t('staffing.fullscreenExit')}` : `⤢ ${t('staffing.fullscreen')}`}
          </button>
        </div>
      </div>

      {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
      {notice && <p className="text-emerald-700 text-sm bg-emerald-50 px-3 py-2 rounded-lg">{notice}</p>}

      {importing && (
        <StaffingImportModal
          projectId={projectId}
          projectName={data.project_name}
          canEdit={canEdit}
          onClose={() => setImporting(false)}
          onImported={(fresh) => { if (fresh) setData(fresh); else load() }}
        />
      )}

      {backfilling && (
        <BackfillModal
          projectId={projectId}
          onClose={() => setBackfilling(false)}
          onApplied={(fresh) => { if (fresh) setData(fresh); else load() }}
        />
      )}

      {replacing && (
        <ReplaceModal
          projectId={projectId}
          row={replacing}
          weeks={weeks}
          members={members}
          onClose={() => setReplacing(null)}
          onDone={(fresh) => setData(fresh)}
        />
      )}

      <RowContextMenu menu={menu} setMenu={setMenu} available={available} t={t}
        onPick={memberId => handleInsertAfter(rows, menu.row, memberId)}
        onFill={hours => handleFill(menu.row, hours)}
        onClear={() => handleClear(menu.row)}
        onRemove={() => handleRemove(menu.row)} />

      <ColumnContextMenu colMenu={colMenu} setColMenu={setColMenu} t={t}
        onAddBefore={() => addWeekAt('start')}
        onAddAfter={() => addWeekAt('end')}
        onRemove={removeWeekAt} />

      {rows.some(r => r.needs_replacement) && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
          <span className="text-amber-600">⚠</span>
          <div className="text-sm text-amber-800">
            <strong className="mr-1">
              {t('staffing.replaceTitle', {
                count: rows.filter(r => r.needs_replacement).length,
              })}
            </strong>
            {rows.filter(r => r.needs_replacement)
              .map(r => `${r.name} (${num(r.future_hours)} h)`).join(', ')}
          </div>
        </div>
      )}

      {/* ── Hours per month and person ───────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">{t('clevel:scenarios.panel.chart')}</h3>
        <ProjectMonthlyChart staffing={data} />
      </div>

      {/* ── Totals vs budget ────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
        <span>
          <span className="text-gray-500 text-xs">{t('staffing.plannedTotal')} </span>
          <span className="font-semibold tabular-nums">{num(data.total_hours)} HH</span>
        </span>
        <span>
          <span className="text-gray-500 text-xs">{t('staffing.budgetCurrent')} </span>
          <span className="tabular-nums">
            {data.hours_budget != null ? `${num(data.hours_budget)} HH` : '—'}
          </span>
        </span>
        {budgetMatches ? (
          <span className="text-xs text-emerald-700">{t('staffing.budgetMatches')}</span>
        ) : canEdit && data.total_hours > 0 && (
          <button
            onClick={() => guard(() => applyStaffingToBudget(projectId))}
            className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            {t('staffing.applyToBudget', { hours: num(data.total_hours) })}
          </button>
        )}
      </div>

      {/* ── The grid ────────────────────────────────────────────── */}
      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-14 text-center text-sm text-gray-400">
          {t('staffing.noRows')}
        </div>
      ) : (
        <div className={`bg-white rounded-xl border border-gray-200 overflow-auto ${
          fullscreen ? 'max-h-[calc(100vh-13rem)]' : 'max-h-[70vh]'}`}>
          <table className="text-xs border-separate border-spacing-0">
            <thead>
              <tr>
                <th {...fixed(0, NAME_W, `${stickyHead} text-left px-3 py-2 font-semibold text-gray-600 z-30`)}>
                  {t('staffing.resource')}
                </th>
                <th {...fixed(NAME_W, ROLE_W, `${stickyHead} text-left px-2 py-2 font-semibold text-gray-600 z-30`)}>
                  {t('staffing.role')}
                </th>
                <th {...fixed(NAME_W + ROLE_W, META_W, `${stickyHead} text-center px-1 py-2 font-semibold text-gray-600 z-30`)}
                    title={t('staffing.weeklyHoursHint')}>
                  {t('staffing.weeklyHours')}
                </th>
                {weeks.map(w => (
                  <th key={w.monday}
                      ref={el => {
                        if (el) weekRefs.current.set(w.monday, el)
                        else weekRefs.current.delete(w.monday)
                      }}
                      className={`${stickyHead} px-1 py-2 font-medium text-gray-500 text-center`}
                      style={{ width: CELL_W, minWidth: CELL_W }}
                      title={t('staffing.maxDaysHint', { days: w.business_days })}
                      onContextMenu={e => {
                        if (!canEdit) return
                        e.preventDefault()
                        setColMenu({
                          x: e.clientX, y: e.clientY, week: w,
                          isFirst: isTrueFirstWeek(w), isLast: isTrueLastWeek(w),
                        })
                      }}>
                    {canEdit && isTrueFirstWeek(w) && (
                      <div className="flex items-center justify-center gap-1 mb-0.5">
                        <button onClick={() => addWeekAt('start')} disabled={extending}
                                title={t('staffing.addWeekBefore')}
                                className="text-gray-300 hover:text-gray-700 leading-none">+</button>
                        <button onClick={() => removeWeekAt('start')} disabled={extending}
                                title={t('staffing.removeWeek')}
                                className="text-gray-300 hover:text-red-600 leading-none">×</button>
                      </div>
                    )}
                    <div>S{isoWeekNumber(w.monday)}</div>
                    <div className="text-[10px] text-gray-400">{weekLabel(w.monday)}</div>
                    {canEdit && isTrueLastWeek(w) && (
                      <div className="flex items-center justify-center gap-1 mt-0.5">
                        <button onClick={() => removeWeekAt('end')} disabled={extending}
                                title={t('staffing.removeWeek')}
                                className="text-gray-300 hover:text-red-600 leading-none">×</button>
                        <button onClick={() => addWeekAt('end')} disabled={extending}
                                title={t('staffing.addWeekAfter')}
                                className="text-gray-300 hover:text-gray-700 leading-none">+</button>
                      </div>
                    )}
                  </th>
                ))}
                {months.map(m => (
                  <th key={`${m.year}-${m.month}`}
                      className={`${stickyHead} px-2 py-2 font-medium text-gray-500 text-center bg-gray-50`}
                      style={{ minWidth: 64 }}>
                    {t('staffing.monthHours', { month: monthLabel(m.year, m.month) })}
                  </th>
                ))}
                <th className={`${stickyHead} px-3 py-2 font-semibold text-gray-700 text-right`}
                    style={{ minWidth: 78 }}>
                  {t('staffing.total')}
                </th>
                {canEdit && <th className={`${stickyHead}`} style={{ minWidth: 34 }} />}
              </tr>
            </thead>

            <tbody>
              {rows.map((row, i) => {
                const bg = i % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'
                const dragProps = canEdit ? {
                  draggable: true,
                  onDragStart: () => setDragging(row.id),
                  onDragEnd: () => setDragging(null),
                  onDragOver: (e) => { if (dragging != null) e.preventDefault() },
                  onDrop: () => handleDrop(rows, row.id),
                } : {}
                return (
                  <tr key={row.id} className={bg} style={{ height: rowH }}
                      onContextMenu={e => {
                        if (!canEdit) return
                        e.preventDefault()
                        setMenu({ x: e.clientX, y: e.clientY, row, mode: 'root' })
                      }}>
                    <td {...fixed(0, NAME_W, `${bg} px-3 py-1 border-b border-gray-100`)}>
                      <div {...dragProps}
                           className={`flex items-center gap-1 min-w-0 ${
                             dragging === row.id ? 'opacity-40' : ''}`}>
                        {canEdit && (
                          <span className="shrink-0 text-gray-300 cursor-move select-none"
                                title={t('staffing.reorderHint')}>⠿</span>
                        )}
                        {/* The row stays -- what this person was committed to
                            is history -- but a commitment still ahead of us
                            is a hole somebody has to fill. */}
                        {row.needs_replacement && (
                          <span className="shrink-0 text-amber-600 text-xs"
                                title={t(row.member_active
                                  ? 'staffing.replaceNotPlannable'
                                  : 'staffing.replaceLeft',
                                  { name: row.name, hours: num(row.future_hours) })}>
                            ⚠
                          </span>
                        )}
                        <span className={`truncate ${row.needs_replacement
                          ? 'text-amber-700' : 'text-gray-800'}`} title={row.name}>
                          {row.name}
                        </span>
                      </div>
                      {row.dedication && (
                        <div className="truncate text-[10px] text-gray-400" title={row.dedication}>
                          {row.dedication}
                        </div>
                      )}
                    </td>
                    <td {...fixed(NAME_W, ROLE_W, `${bg} px-2 py-1 border-b border-gray-100`)}>
                      <span className="truncate block text-gray-500" title={row.activities || row.role || ''}>
                        {row.role || '—'}
                      </span>
                    </td>
                    <td {...fixed(NAME_W + ROLE_W, META_W, `${bg} px-1 py-1 border-b border-gray-100 text-center`)}>
                      {/* Read-only here: a person's weekly hours belong to the
                          team roster, not to any one project's plan. */}
                      <span className="tabular-nums text-gray-500">
                        {num(row.weekly_hours)}
                      </span>
                    </td>

                    {row.weeks.filter(cell => visibleMondays.has(cell.monday)).map(cell => (
                      <td key={cell.monday}
                          className="px-0.5 py-1 border-b border-gray-100 text-center align-middle"
                          style={{ width: CELL_W, minWidth: CELL_W }}
                          title={t('staffing.maxHoursHint', { hours: num(cell.max_hours) })}>
                        <HoursCell
                          value={cell.hours}
                          max={cell.max_hours}
                          disabled={!canEdit}
                          onCommit={v => guard(() => setStaffingWeeks(projectId, row.id,
                            { week_start: cell.monday, hours: v }))}
                        />
                        {showActual && (
                          <div
                            className={`text-[10px] leading-tight tabular-nums ${
                              cell.actual_hours == null ? 'text-gray-300'
                                : cell.actual_hours < cell.hours ? 'text-amber-700'
                                : 'text-emerald-700'}`}
                            title={cell.actual_hours == null
                              ? t('staffing.noActual') : t('staffing.actualHint')}
                          >
                            {cell.actual_hours == null
                              ? (cell.hours ? '—' : '')
                              : num(cell.actual_hours)}
                          </div>
                        )}
                      </td>
                    ))}

                    {row.months.filter(m => visibleMonthKeys.has(`${m.year}-${m.month}`)).map(m => (
                      <td key={`${m.year}-${m.month}`}
                          className="px-2 py-1 border-b border-gray-100 text-right tabular-nums text-gray-500 bg-gray-50/60">
                        {m.hours ? num(m.hours) : ''}
                      </td>
                    ))}
                    <td className="px-3 py-1 border-b border-gray-100 text-right tabular-nums font-medium text-gray-900">
                      {num(row.total_hours)}
                    </td>
                    {canEdit && (
                      <td className="px-1 py-1 border-b border-gray-100 text-center whitespace-nowrap">
                        <button onClick={() => handleClear(row)}
                                title={t('staffing.clearRow')}
                                disabled={!row.total_hours}
                                className="mr-1 text-gray-300 hover:text-amber-600 disabled:opacity-30 disabled:hover:text-gray-300">↺</button>
                        <button onClick={() => setReplacing(row)}
                                title={t('staffing.replace')}
                                className={`mr-1 ${row.needs_replacement
                                  ? 'text-amber-600 hover:text-amber-700'
                                  : 'text-gray-300 hover:text-gray-600'}`}>⇄</button>
                        <button onClick={() => handleRemove(row)}
                                title={t('staffing.remove')}
                                className="text-gray-300 hover:text-red-600">×</button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>

            <tfoot>
              <TotalsRow label={t('staffing.totalRow')} bold
                         values={weeks.map(w => w.hours)} months={months}
                         monthValues={months.map(m => m.hours)}
                         total={data.total_hours} canEdit={canEdit} fixed={fixed} />
              <TotalsRow label={t('staffing.headcount')}
                         values={weeks.map(w => w.headcount)} months={months}
                         canEdit={canEdit} fixed={fixed} />
              <TotalsRow label={t('staffing.fte')} digits={1}
                         values={weeks.map(w => w.fte)} months={months}
                         canEdit={canEdit} fixed={fixed} />
              <TotalsRow label={t('staffing.businessDays')} muted
                         values={weeks.map(w => w.business_days)} months={months}
                         canEdit={canEdit} fixed={fixed} />
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
